import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, FileText, X, Search, ExternalLink, Loader2 } from 'lucide-react';
import type { Protocol } from '../../types';
import api from '../../../services/api';

interface ProtocolSelectorProps {
  protocols: Protocol[];
  selectedProtocol: Protocol | null;
  onSelect: (protocol: Protocol | null) => void;
  disabled?: boolean;
}

export function ProtocolSelector({ protocols, selectedProtocol, onSelect, disabled = false }: ProtocolSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingProtocol, setIsLoadingProtocol] = useState(false);
  const [protocolError, setProtocolError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filter protocols based on search term
  const filteredProtocols = protocols.filter(protocol => {
    const search = searchTerm.toLowerCase();
    return (
      protocol.protocol_number.toLowerCase().includes(search) ||
      protocol.sponsor_name?.toLowerCase().includes(search) ||
      protocol.study_nickname?.toLowerCase().includes(search)
    );
  });

  const handleSelect = (protocol: Protocol) => {
    onSelect(protocol);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(null);
  };

  const handleViewProtocol = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selectedProtocol || isLoadingProtocol) return;

    setIsLoadingProtocol(true);
    setProtocolError(null);

    try {
      // Try fetching protocol-type documents first
      let response = await api.get(
        `/api/intelligence/protocols/${selectedProtocol.id}/documents?document_type=protocol`
      );
      let docs = response.data;

      // Fallback: fetch all documents if no protocol-type docs found
      if (!docs || docs.length === 0) {
        response = await api.get(
          `/api/intelligence/protocols/${selectedProtocol.id}/documents`
        );
        docs = response.data;
      }

      if (!docs || docs.length === 0) {
        setProtocolError('No documents found for this protocol');
        setTimeout(() => setProtocolError(null), 4000);
        return;
      }

      // Download the first document as a blob
      const docId = docs[0].id;
      const downloadResponse = await api.get(
        `/api/intelligence/documents/${docId}/download`,
        { responseType: 'blob' }
      );

      const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (err: any) {
      console.error('Error viewing protocol document:', err);
      setProtocolError(err.response?.data?.error || 'Failed to load protocol document');
      setTimeout(() => setProtocolError(null), 4000);
    } finally {
      setIsLoadingProtocol(false);
    }
  }, [selectedProtocol, isLoadingProtocol]);

  return (
    <div className="flex-shrink-0 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800" ref={dropdownRef}>
      <div className="px-4 py-3">
        <label className="block text-xs font-medium text-gray-500 mb-1.5 dark:text-gray-400">
          Protocol Context
        </label>

        {/* Selector button */}
        <div
          onClick={() => !disabled && setIsOpen(!isOpen)}
          role="button"
          className={`w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg transition-colors duration-150 text-left dark:bg-gray-700/50 dark:border-gray-600 ${disabled ? 'opacity-75 cursor-default' : 'hover:bg-gray-100 cursor-pointer dark:hover:bg-gray-700'}`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={16} className="text-gray-400 flex-shrink-0" />
            {selectedProtocol ? (
              <div className="min-w-0">
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {selectedProtocol.protocol_number}
                </span>
                {selectedProtocol.sponsor_name && (
                  <span className="text-gray-500 ml-1.5 dark:text-gray-400">
                    ({selectedProtocol.sponsor_name})
                  </span>
                )}
              </div>
            ) : (
              <span className="text-gray-500 dark:text-gray-400">
                Select a protocol...
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {selectedProtocol && (
              <>
                <button
                  onClick={handleViewProtocol}
                  disabled={isLoadingProtocol}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-100 rounded transition-colors duration-150 dark:text-primary-400 dark:hover:bg-primary-900/40"
                  title="View protocol document"
                >
                  {isLoadingProtocol ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <ExternalLink size={14} />
                  )}
                  View Protocol
                </button>
                {!disabled && (
                  <button
                    onClick={handleClear}
                    className="p-1 hover:bg-gray-200 rounded dark:hover:bg-gray-600"
                    title="Clear selection"
                  >
                    <X size={14} className="text-gray-400" />
                  </button>
                )}
              </>
            )}
            <ChevronDown
              size={16}
              className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </div>

        {/* Protocol error message */}
        {protocolError && (
          <div className="mt-1.5 text-xs text-red-600 bg-red-50 px-3 py-1.5 rounded-md dark:text-red-400 dark:bg-red-900/20">
            {protocolError}
          </div>
        )}

        {/* Dropdown */}
        {isOpen && !disabled && (
          <div className="absolute z-50 mt-1 w-[calc(100%-2rem)] max-w-md bg-white border border-gray-200 rounded-lg shadow-lg dark:bg-gray-800 dark:border-gray-700">
            {/* Search input */}
            <div className="p-2 border-b border-gray-100 dark:border-gray-700">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search protocols..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                />
              </div>
            </div>

            {/* Protocol list */}
            <div className="max-h-64 overflow-y-auto">
              {filteredProtocols.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  {searchTerm ? 'No protocols match your search' : 'No protocols available'}
                </div>
              ) : (
                filteredProtocols.map((protocol) => (
                  <button
                    key={protocol.id}
                    onClick={() => handleSelect(protocol)}
                    className={`w-full px-4 py-2.5 text-left hover:bg-primary-50 transition-colors duration-150 border-b border-gray-50 last:border-0 dark:hover:bg-primary-900/20 dark:border-gray-700/50 ${
                      selectedProtocol?.id === protocol.id ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900 text-sm dark:text-gray-100">
                      {protocol.protocol_number}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 dark:text-gray-400">
                      {protocol.sponsor_name && <span>{protocol.sponsor_name}</span>}
                      {protocol.sponsor_name && protocol.study_nickname && <span> &bull; </span>}
                      {protocol.study_nickname && (
                        <span className="line-clamp-1">{protocol.study_nickname}</span>
                      )}
                    </div>
                    {protocol.site_name && (
                      <div className="text-xs text-primary-600 mt-0.5 dark:text-primary-400">
                        {protocol.site_name}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>

            {/* Hint */}
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 rounded-b-lg dark:bg-gray-700/50 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Protocol is locked for the duration of the chat. Start a new chat to change it.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
